/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, DialogDefaultV2, EmptyState } from '../../../components/v2';
import { canViewJornadaCompliance } from '../../../helpers/jornadaCompliancePermission';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import BaseService from '../../../services/BaseService';
import TableService from '../../../services/TableService';
import CalendarioJornada from './CalendarioJornada';
import DiasViolacaoDialog from './DiasViolacaoDialog';
import KpiStrip from './KpiStrip';
import RankingTable from './RankingTable';
import {
  CICLO_CUSTOM,
  CICLOS,
  EMPTY_REPORT,
  REGIME_TABS,
  applyCicloToFilter,
  buildApiBody,
  buildJornadaListUrl,
  buildSearchFromView,
  filterRanking,
  filterReportByRegime,
  formatPeriodLabel,
  groupDiasProblema,
  getDefaultView,
  isPeriodInvalid,
  normalizeBiResponse,
  normalizeCalendarioDias,
  parseViewFromSearch,
  viewsEqual,
} from './constants';

const RelatorioJornadaV2 = ({ history, location, authData, moduleConfigurations }) => {
  const canView = canViewJornadaCompliance({ authData, moduleConfigurations });
  const [view, setView] = useState(() => parseViewFromSearch(location.search, getDefaultView()));
  const [report, setReport] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const [diasDialog, setDiasDialog] = useState(null);
  const [calendarioDias, setCalendarioDias] = useState([]);
  const [calendarioLoading, setCalendarioLoading] = useState(false);
  const [calendarioError, setCalendarioError] = useState(false);

  const syncUrl = useCallback((next) => {
    const search = buildSearchFromView(next);
    const current = (location.search || '').replace(/^\?/, '');
    if (search === current) return;
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    const fromUrl = parseViewFromSearch(location.search, getDefaultView());
    setView((current) => (viewsEqual(current, fromUrl) ? current : fromUrl));
  }, [location.search]);

  useEffect(() => {
    syncUrl(view);
  }, [view, syncUrl]);

  const updateView = (patch) => {
    const next = { ...view, ...patch };
    setView(next);
    syncUrl(next);
  };

  const apiBody = useMemo(
    () => buildApiBody(view),
    [view.inicio, view.fim],
  );
  const periodInvalid = isPeriodInvalid(view.inicio, view.fim);

  const fetchReport = useCallback(async () => {
    if (!canView || periodInvalid) return;
    setLoading(true);
    setFetchError(false);
    try {
      const response = await TableService.request('reports/jornada/bi', apiBody);
      setReport(normalizeBiResponse(response?.data));
    } catch {
      setReport(EMPTY_REPORT);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [apiBody, canView, periodInvalid]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const reportView = useMemo(
    () => filterReportByRegime(report, view.regime),
    [report, view.regime],
  );

  const ranking = useMemo(
    () => filterRanking(reportView.ranking, view.regra),
    [reportView.ranking, view.regra],
  );

  const calendario = useMemo(() => {
    if (view.visao !== 'calendario') return null;
    if (!view.inspetorId) return { mode: 'geral' };
    const row = (report.ranking || []).find((item) => String(item.inspetorId) === String(view.inspetorId));
    return {
      mode: 'inspetor',
      inspetorId: String(view.inspetorId),
      nome: row?.nome || null,
      resumo: row || null,
    };
  }, [view.visao, view.inspetorId, report.ranking]);

  useEffect(() => {
    if (view.visao !== 'calendario' || !view.inspetorId) {
      setCalendarioDias([]);
      setCalendarioLoading(false);
      setCalendarioError(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setCalendarioLoading(true);
      setCalendarioError(false);
      const params = new URLSearchParams({
        inspetorId: String(view.inspetorId),
        inicio: String(view.inicio),
        fim: String(view.fim),
      });
      try {
        const response = await BaseService.get({
          url: `reports/jornada/calendario?${params.toString()}`,
        });
        if (!cancelled) setCalendarioDias(normalizeCalendarioDias(response?.data, view.inspetorId));
      } catch {
        if (!cancelled) {
          setCalendarioDias([]);
          setCalendarioError(true);
        }
      } finally {
        if (!cancelled) setCalendarioLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [view.visao, view.inspetorId, view.inicio, view.fim]);

  const diasCalendario = useMemo(() => {
    if (!calendario) return [];
    if (calendario.mode === 'geral') return reportView.dias;
    return calendarioDias;
  }, [calendario, calendarioDias, reportView.dias]);

  const diasProblema = useMemo(
    () => (diasDialog ? groupDiasProblema(reportView.dias, diasDialog.inspetorId) : []),
    [reportView.dias, diasDialog],
  );

  const openJornada = (jornadaId, inspetorId) => {
    history.push(buildJornadaListUrl({
      inicio: view.inicio,
      fim: view.fim,
      inspetorId: inspetorId || diasDialog?.inspetorId || calendario?.inspetorId,
      jornadaId,
    }));
  };

  const openJornadaNovaAba = (jornadaId, inspetorId) => {
    const url = buildJornadaListUrl({
      inicio: view.inicio,
      fim: view.fim,
      inspetorId: inspetorId || view.inspetorId,
      jornadaId,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeCalendario = () => updateView({ visao: null, inspetorId: null });

  const openFolha = (row) => {
    const url = buildJornadaListUrl({
      inicio: view.inicio,
      fim: view.fim,
      inspetorId: row.inspetorId,
      listagem: true,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onApplyFilter = (filterData) => {
    const next = applyCicloToFilter(filterData);
    if (next.ciclo === CICLO_CUSTOM && isPeriodInvalid(next.inicio, next.fim)) {
      setFilterError('A data de início deve ser anterior ou igual à data de fim.');
      return;
    }
    setFilterError(null);
    setOpenFilter(false);
    updateView({
      ciclo: next.ciclo,
      inicio: next.inicio,
      fim: next.fim,
    });
  };

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="O compliance de jornada não está disponível para o seu usuário. Solicite acesso ao administrador."
        />
      </Box>
    );
  }

  const periodo = formatPeriodLabel(view.inicio, view.fim);

  return (
    <Box>
      <Stack spacing={3}>
        {loading && <LinearProgress />}
        <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h4">Compliance de Jornada</Typography>
                <Typography variant="body2" color="text.secondary">
                  {periodo ? `Ciclo ${periodo}` : 'Período'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<CalendarMonthOutlinedIcon />}
                  onClick={() => updateView({ visao: 'calendario', inspetorId: null })}
                >
                  Ver no calendário
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<FilterListOutlinedIcon />}
                  onClick={() => setOpenFilter(true)}
                >
                  Filtros
                </Button>
              </Stack>
            </Stack>

            <Tabs
              value={view.regime || 'GERAL'}
              onChange={(_, next) => updateView({ regime: next === 'GERAL' ? null : next })}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {REGIME_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: 44 }}
                />
              ))}
            </Tabs>

            {periodInvalid && (
              <Alert severity="warning">A data de início deve ser anterior ou igual à data de fim.</Alert>
            )}
            {fetchError && (
              <Alert severity="error">
                Não foi possível carregar o relatório. Confira se o endpoint de compliance de jornada já está no ar.
              </Alert>
            )}

            <KpiStrip
              kpis={reportView.kpis}
              loading={loading}
              activeRegra={view.regra}
              onSelect={(regra) => updateView({ regra })}
            />

            <RankingTable
              rows={ranking}
              loading={loading}
              onOpenCalendario={(row) => updateView({
                visao: 'calendario',
                inspetorId: String(row.inspetorId),
              })}
              onOpenFolha={openFolha}
              onOpenDias={setDiasDialog}
            />
        </>

        <DialogDefaultV2
          open={Boolean(calendario)}
          handlerCancel={closeCalendario}
          handlerConfirm={closeCalendario}
          title={calendario?.mode === 'geral' ? 'Calendário geral' : (calendario?.nome || 'Calendário')}
          readOnly
          maxWidth="lg"
          fullWidth
        >
          {calendario && (
            <CalendarioJornada
              mode={calendario.mode}
              dias={diasCalendario}
              inicio={view.inicio}
              fim={view.fim}
              onOpenJornada={openJornadaNovaAba}
              resumo={calendario.resumo}
              loading={calendario.mode === 'inspetor' && calendarioLoading}
              loadError={calendario.mode === 'inspetor' && calendarioError}
            />
          )}
        </DialogDefaultV2>

        <DiasViolacaoDialog
          open={Boolean(diasDialog)}
          nome={diasDialog?.nome}
          row={diasDialog}
          dias={diasProblema}
          onClose={() => setDiasDialog(null)}
          onOpenJornada={openJornada}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => {
            setOpenFilter(false);
            setFilterError(null);
          }}
          title="Filtros"
          fullWidth
          defaultFilter={getDefaultView()}
          appliedFilter={view}
          confirmButtonText="Aplicar"
          fields={[
            {
              field: 'useSelectOption',
              key: 'ciclo',
              label: 'Ciclo',
              options: CICLOS,
              md: 12,
              xs: 12,
              showEmpty: false,
              apply: applyCicloToFilter,
            },
            {
              field: 'useDates',
              md: 6,
              disabled: (filter) => filter.ciclo !== CICLO_CUSTOM,
            },
          ]}
          onFilter={onApplyFilter}
        />
        <Snackbar
          open={Boolean(filterError)}
          autoHideDuration={6000}
          onClose={() => setFilterError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="warning" onClose={() => setFilterError(null)} sx={{ width: '100%' }}>
            {filterError}
          </Alert>
        </Snackbar>
      </Stack>
    </Box>
  );
};

RelatorioJornadaV2.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

export default withRouter(connect(mapStateToProps)(RelatorioJornadaV2));
